import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAuditLogs } from '../services/auditService';
import { AuditLog, EmployeeRecord } from '../types';
import { employeeService } from '../services/employeeService';
import { FiClock, FiUser, FiEdit, FiPlusCircle, FiTrash2 } from 'react-icons/fi';

const AuditLogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [employee, setEmployee] = useState<EmployeeRecord | null>(null);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getAuditLogs(id)
        .then(setLogs)
        .catch(error => console.error('Failed to fetch audit logs', error))
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    employeeService.getEmployee(id)
      .then(setEmployee)
      .catch(error => console.error('Failed to fetch employee for audit context', error));
  }, [id]);

  const renderIcon = (action: string) => {
    switch (action) {
      case 'create':
        return <FiPlusCircle className="h-5 w-5 text-green-500" />;
      case 'update':
        return <FiEdit className="h-5 w-5 text-blue-500" />;
      case 'delete':
        return <FiTrash2 className="h-5 w-5 text-red-500" />;
      default:
        return <FiEdit className="h-5 w-5 text-gray-500" />;
    }
  };

  const renderChange = (log: AuditLog) => {
    if (log.action === 'create') {
      return <p>Record created.</p>;
    }
    if (log.action === 'delete') {
      return <p>Record deleted.</p>;
    }
    if (log.fieldChanges && log.fieldChanges.length > 0) {
      return (
        <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
          {log.fieldChanges.map((change, index) => (
            <li key={index}>
              <strong>{change.field}:</strong> changed from{' '}
              <span className="font-mono bg-gray-200 px-1 rounded">{JSON.stringify(change.oldValue)}</span> to{' '}
              <span className="font-mono bg-gray-200 px-1 rounded">{JSON.stringify(change.newValue)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return <p>No specific field changes logged.</p>;
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Audit Trail for Employee #{id}</h1>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="border border-primary-200 rounded-lg p-4 flex items-start gap-4"
              aria-hidden="true"
            >
              <div
                className="relative overflow-hidden rounded-full bg-primary-300 h-10 w-10 flex-shrink-0"
              >
                <div
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"
                  style={{ animationDelay: `${i * 80}ms` }}
                />
              </div>
              <div className="flex-1 space-y-2">
                <div
                  className="relative overflow-hidden rounded bg-primary-300 h-4 w-1/3"
                >
                  <div
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"
                    style={{ animationDelay: `${i * 80 + 100}ms` }}
                  />
                </div>
                <div
                  className="relative overflow-hidden rounded bg-primary-300 h-3 w-2/3"
                >
                  <div
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer"
                    style={{ animationDelay: `${i * 80 + 200}ms` }}
                  />
                </div>
              </div>
            </div>
          ))}
          <span className="sr-only">Loading audit logs</span>
        </div>
      )}

      {!isLoading && (
        <>
      
      {employee && (
        <div className="mb-6 p-4 border border-blue-100 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Created by:</span>{' '}
            {employee.createdByName || (employee.createdBy === 'candidate' ? 'Candidate' : employee.createdBy || 'Unknown')}{' '}
            on {new Date(employee.createdAt).toLocaleString()}
          </p>
        </div>
      )}

      <div className="relative border-l-2 border-gray-200">
        {logs.length === 0 ? (
          <p className="ml-6">No audit logs found for this employee.</p>
        ) : (
          logs.map(log => (
            <div key={log.timestamp} className="mb-8 ml-6">
              <span className="absolute -left-3 flex items-center justify-center w-6 h-6 bg-white rounded-full ring-4 ring-white">
                {renderIcon(log.action)}
              </span>
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-semibold text-gray-800 capitalize">{log.action.replace('_', ' ')}</p>
                  <div className="flex items-center text-xs text-gray-500">
                    <FiClock className="mr-1" />
                    <span>{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiUser className="mr-2" />
                  <span>{log.userName} (ID: {log.userId})</span>
                </div>
                <div>{renderChange(log)}</div>
              </div>
            </div>
          ))
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default AuditLogPage;
